// @flow

import * as React from 'react'
import moment from 'moment'
import styled, { css } from 'styled-components'
import { AlignmentTypes, Images, DISPLAY_DATE_FORMAT } from '@/constants'
import jsStyles from './styles'
import styles from './styles.scss'

const DayButton = styled.button`
  color: ${styles.orange8};
  ${props =>
    props.active &&
    css`
      background-color: ${styles.orange8} !important;
      > span {
        color: ${styles.white} !important;
      }
    `}
`

type CalendarProps = {|
  alignment?: $Values<typeof AlignmentTypes>,
  onChange: (value: string) => void,
  requestClose: () => void,
  ariaLabel: string,
  value: ?string,
  beforeToday: ?boolean,
  afterToday: ?boolean,
  onKeyDown?: ?(e: DOMEvent) => void,
  minDate?: ?moment,
  maxValue: ?number,
  maxValueUnit:  ?"year" | 'day' | 'month',
|}

type CalendarState = {|
  month: moment,
|}

export class Calendar extends React.Component<CalendarProps, CalendarState> {
  today: moment = moment()

  static defaultProps = {
    alignment: AlignmentTypes.center,
    onKeyDown: null,
    minDate: undefined,
  }

  constructor(props: CalendarProps) {
    super(props)
    const { value } = props
    this.state = {
      month: value ? moment(value, DISPLAY_DATE_FORMAT) : moment(),
    }
  }

  componentDidUpdate(prevProps: CalendarProps) {
    const { value } = this.props
    const month = moment(value)
    if (value !== prevProps.value && month.isValid()) {
      // The shortest a string could be is six characters for example 1/1/19
      if (month.isValid() && value && value.toString().length >= 6) {
        this.setState({ month }) // eslint-disable-line react/no-did-update-set-state
      }
    }
  }

  nextMonth = () => {
    const { afterToday, maxValue, maxValueUnit } = this.props
    const { month } = this.state
    const validMax = maxValue && maxValueUnit ? moment().add(maxValue, maxValueUnit).endOf('month').isAfter(month.endOf('month')) : true
    if ((afterToday || month.endOf('month').isBefore(moment().endOf('month'))) && validMax) {
      this.setState(prevState => ({ month: prevState.month.add(1, 'month') }))
    }
  }

  previousMonth = () => {
    const { beforeToday } = this.props
    const { month } = this.state
    if (month.startOf('month').isAfter(this.today) || beforeToday) {
      this.setState(prevState => ({
        month: prevState.month.subtract(1, 'month'),
      }))
    }
  }

  selectDate = (e: DOMEvent) => {
    e.persist()
    const { onChange, requestClose } = this.props
    const { value } = e.currentTarget
    onChange(value)
    requestClose()
  }

  generateMonth = () => {
    const { month } = this.state
    const { value, minDate } = this.props
    let current = moment(month)
      .startOf('month')
      .startOf('week')
    const end = moment(month)
      .endOf('month')
      .endOf('week')
    const monthRows = []
    let weekRow = []

    while (current.isBefore(end)) {
      const isCurrent = current.month() === month.month()
      const key = `${current.month()}-${current.date()}`
      const active = current.format(DISPLAY_DATE_FORMAT) === value

      // Create the day
      const square = (
        <DayButton
          active={active}
          className={styles.day}
          key={key}
          // $FlowFixMe
          disabled={!isCurrent || (minDate && current < minDate)}
          type="button"
          onClick={this.selectDate}
          value={current.format(DISPLAY_DATE_FORMAT)}
        >
          <span>{current.date()}</span>
        </DayButton>
      )
      // Add it to our matrix
      if (weekRow.length % 7 === 0) {
        // End of the week
        if (weekRow.length) {
          monthRows.push([...weekRow])
        }
        weekRow = [square]
      } else {
        weekRow.push(square)
      }
      current = moment(current.add(1, 'day'))
    }
    // Push the last row
    monthRows.push([...weekRow])
    return monthRows
  }

  renderMonth = (): Array<React.Node> => {
    const monthMatrix = this.generateMonth()
    return monthMatrix.map(m => (
      <div key={`week-${m[0]?.key || 'key'}`} className={styles.week}>
        {m}
      </div>
    ))
  }

  render = () => {
    const { alignment, ariaLabel, onKeyDown } = this.props
    const { month } = this.state
    return (
      <div
        className={styles.calendarContainer}
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        css={jsStyles.alignment(alignment)}
        role="input" // eslint-disable-line jsx-a11y/aria-role
      >
        <div className={styles.header}>
          <button className={styles.toggle} onClick={this.previousMonth} type="button">
            <img src={Images.arrowLeft} alt="left-arrow" />
          </button>
          <p>{month.format('MMMM YYYY')}</p>
          <button className={styles.toggle} onClick={this.nextMonth} type="button">
            <img src={Images.arrowRight} alt="right-arrow" />
          </button>
        </div>
        <div className={styles.month}>{this.renderMonth()}</div>
      </div>
    )
  }
}

export default Calendar
